import React, { useEffect } from 'react';
import { useTenantConfig } from './lib/useTenantConfig';
import Navbar from './components/Navbar';
import HeroDeck from './components/HeroDeck';
import Schedule from './components/Schedule';
import Releases from './components/Releases';

export default function App() {
  const { config, error, loading } = useTenantConfig();

  useEffect(() => {
    const theme = (config?.theme ?? 'dark').toLowerCase();
    document.documentElement.setAttribute('data-theme', theme);

    const fw = (config?.navWeight ?? 'medium').toLowerCase();
    document.documentElement.setAttribute('data-navfw', fw);

    if (config) {
      document.title =
        config.brand.pageTitle ??
        config.seo?.title ??
        config.brand.headerText ??
        config.brand.name ??
        'Koloman';
    }
  }, [config]);

  // trava a altura do hero no carregamento (evita pular quando a barra some/aparece)
  useEffect(() => {
    const lockVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh-lock', `${vh}px`);
    };
    lockVH();
    const t = setTimeout(lockVH, 250); // segunda medição quando a UI estabiliza
    return () => clearTimeout(t);
  }, []);

  if (loading) return <div className="kl-section">Carregando…</div>;
  if (error) return <div className="kl-section" style={{ color: 'crimson' }}>Erro: {error}</div>;
  if (!config) return <div className="kl-section">Sem config</div>;

  // Hero object-position via JSON (supports 'middle' alias)
  const hx = (config.hero.pos?.x ?? 'center').replace('middle', 'center');
  const hy = (config.hero.pos?.y ?? 'center').replace('middle', 'center');
  const objPos = `${hx} ${hy}`;

  return (
    <main>
      <div id="top" />
      <Navbar config={config} />

      {/* HERO */}
      <section className="kl-hero">
        {config.hero.type === 'image' ? (
          <img src={config.hero.src} alt="hero" className="kl-hero-media" style={{ objectPosition: objPos }} />
        ) : (
          <video src={config.hero.src} autoPlay muted loop playsInline className="kl-hero-media" style={{ objectPosition: objPos }} />
        )}

        {/* CDJ: controles fixos */}
        {config.hero.deck && (config.hero.deck.enabled ?? true) && config.hero.deck.src ? (
          <HeroDeck
            src={config.hero.deck.src}
            cuePoint={config.hero.deck.cuePoint}
            title={config.hero.deck.title}
          />
        ) : null}
      </section>

      {/* SEÇÕES */}
      {config.sections.map((s, i) => {
        if (s.type === 'bio') {
          return (
            <section key={i} className="kl-section">
              {s.title && <h2 style={{ marginTop: 0 }}>{s.title}</h2>}
              <p className="kl-muted" style={{ maxWidth: 780 }}>{s.text}</p>
            </section>
          );
        }

        if (s.type === 'tour') {
          return <Schedule key={i} section={s as any} />;
        }

        if (s.type === 'releases') {
          return <Releases key={i} section={s as any} />;
        }

        if (s.type === 'gallery') {
          return (
            <section key={i} id="gallery" className="kl-section">
              <h2 style={{ marginTop: 0, letterSpacing: 1 }}>GALLERY</h2>
              <div className="kl-grid" style={{ marginTop: 12 }}>
                {s.items.map((it, idx) => {
                  const src = typeof it === 'string' ? it : it.src;
                  const caption = typeof it === 'string' ? '' : (it.caption ?? '');
                  return (
                    <figure key={idx} className="kl-card" style={{ margin: 0 }}>
                      <img src={src} alt={caption || `gallery-${idx}`} style={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }} />
                      {caption && <figcaption className="kl-muted" style={{ padding: '10px 12px', fontSize: 12 }}>{caption}</figcaption>}
                    </figure>
                  );
                })}
              </div>
            </section>
          );
        }

        if (s.type === 'player') {
          return (
            <section key={i} className="kl-section">
              <h2 style={{ marginTop: 0, letterSpacing: 1 }}>OUÇA</h2>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {s.providers.map((p) => {
                  const href = (s as any).links?.[p];
                  if (!href) return null;
                  return (
                    <a key={p} href={href} target="_blank" rel="noreferrer" className="kl-pill">
                      {p}
                    </a>
                  );
                })}
              </div>
            </section>
          );
        }

        if (s.type === 'cta') {
          const href = s.action === 'whatsapp' ? `https://wa.me/${s.to.replace(/\D/g, '')}` : s.action === 'email' ? `mailto:${s.to}` : s.to;
          return (
            <footer key={i} id="contact" className="kl-section kl-footer">
              <h3 style={{ marginTop: 0, marginBottom: 12 }}>Contato</h3>
              <a href={href} className="kl-btn">{s.label}</a>
              <div className="kl-muted" style={{ marginTop: 20, fontSize: 12 }}>
                © {new Date().getFullYear()} {config.brand.footerText ?? config.brand.name ?? 'Koloman Lab'}
              </div>
            </footer>
          );
        }

        return null;
      })}
    </main>
  );
}